import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { getDBTypesByProvider } from '../utils/index'
import { SelectWorkflowComponent } from './common/selectcomponent'

class RefactorColumns extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.refactorcolumns',
        group: 'Data Preparation',
        title: 'Edit Schema',
        description: `
      This component allows to change to names and data types of table columns.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'columns',
          type: WorkflowComponentParamType.SelectColumnType,
          title: 'Column name and type',
          parent: 'source',
          noDefault: true,
          providers: [Provider.BigQuery, Provider.Snowflake, Provider.Redshift, Provider.Postgres]
        },
        {
          name: 'databricks_columns',
          type: WorkflowComponentParamType.SelectColumnType,
          title: 'Column name and type',
          parent: 'source',
          noDefault: true,
          dataType: [
            ColumnDataType.Number,
            ColumnDataType.String,
            ColumnDataType.Boolean,
            ColumnDataType.Date,
            ColumnDataType.Time,
            ColumnDataType.Datetime,
            ColumnDataType.Timestamp
          ],
          providers: [Provider.DatabricksRest]
        }
      ]
    )
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    const columns =
      connection.type === Provider.DatabricksRest ? (params.databricks_columns as string) : (params.columns as string)
    const lines: Array<string> = columns?.split('\n')?.map((x) => x.trim()) || []
    const newCols: Array<string> = []
    for (const line of lines) {
      const tokens = line.split(',').map((x) => x.trim())
      const type = tokens[2]
      if (connection.type === Provider.BigQuery) {
        newCols.push(`SAFE_CAST(${tokens[0]} AS ${type}) AS ${tokens[1]}`)
      } else if (connection.type === Provider.Snowflake && type === 'GEOGRAPHY') {
        newCols.push(`TRY_TO_GEOGRAPHY(${tokens[0]}) AS ${tokens[1]}`)
      } else {
        newCols.push(`CAST(${tokens[0]} AS ${type}) AS ${tokens[1]}`)
      }
    }
    if (newCols.length === 0) {
      newCols.push('1')
    }
    if (connection.type === Provider.DatabricksRest) {
      return dedent`SELECT /*+ REPARTITION(40) */
                      ${newCols.join(',\n')}
                    FROM ${connection.quoteName(params.source as string)}`
    } else {
      return dedent`SELECT
                      ${newCols.join(',\n')}
                    FROM ${connection.quoteName(params.source as string)}`
    }
  }

  getUIProperties(provider?: Provider): Record<string, unknown> {
    return getDBTypesByProvider(provider)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '2'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ params, connection }: GetOutputSchemaParams): TableSchema {
    const schema: TableSchema = []
    const columnsParam =
      connection.type === Provider.DatabricksRest ? (params.databricks_columns as string) : (params.columns as string)
    const lines: Array<string> = columnsParam?.split('\n')?.map((x) => x.trim()) || []
    for (const line of lines) {
      const tokens = line.split(',').map((x) => x.trim())
      const name = tokens[1]
      const db_type = tokens[2]
      const type = connection.dataProviderTypeToColumnType(db_type)
      schema.push({ name, type, db_type })
    }
    return schema
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { RefactorColumns }
